/** Strings of the detail popups' figures: the stored match rows, the diagnosis figures (meters and percent chips, each with a
 * one-line explanation and a "how it is computed" tooltip), the timeline and the cause track record. Re-exported by catalogText.js. */

const pts = (n) => `${n > 0 ? "+" : n < 0 ? "−" : ""}${Math.abs(n)} pts`;
const ordinal = (n) => {
  const rest = n % 100;
  if (rest >= 11 && rest <= 13) return `${n}th`;
  return `${n}${["th", "st", "nd", "rd"][n % 10] || "th"}`;
};
const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** The read-only rows of a stored match snapshot. */
export const STORED_MATCH_TEXT = Object.freeze({
  heading: "How it was matched",
  matchedAt: (when) => `Matched when saved · ${when}`,
  degraded: "Matching was not available when this was saved: every line was stored as new.",
  notMatched: "Saved before matching existed, so every line is new.",
  pattern: { existing: (label) => `Matches an existing pattern: ${label}`, existingNoLabel: "Matches an existing pattern", fresh: "New pattern" },
  patternHintExisting: "The lines below were added to that pattern when they were new.",
  patternHintNew: "Nothing similar was in this category: the pattern was added as new.",
  state: Object.freeze({ reused: "Reused", new: "New" }),
  reuses: (label) => `Reuses: ${label}`,
  reusesFact: (label) => `Reuses fact: ${label}`,
  reusedNoLabel: "Reuses an existing line",
  addedNew: "Added as new.",
  changed: "Changed by you",
  changedHint: (suggestedNew) => (suggestedNew ? "Arta suggested adding it as new; you chose an existing entry." : "Arta suggested a different entry; you chose this one."),
  similar: (percent) => `${percent}% similar`,
  similarHint: "Similarity of this line to the chosen catalog entry when it was saved (100% = the same wording).",
  kind: Object.freeze({ fact: "Matched fact", symptom: "Matched symptom", step: "Matched step" }),
  source: Object.freeze({ fact: "Fact", note: "Note", symptom: "Symptom", step: "Step" }),
  sourceHint: Object.freeze({
    fact: "A fact gathered in the chat", note: "Noted from the engineer's own words", symptom: "A symptom line", step: "A troubleshooting step",
  }),
  unsure: "Unsure",
  unsureHint: "Stated with some doubt",
  fromRunbook: "from runbook",
  learned: "learned",
  lines: Object.freeze({ symptoms: "Symptoms", steps: "Troubleshooting steps", cause: "Root cause", fix: "Fix or workaround" }),
  none: "None recorded",
  issues: "Issues",
});

/** The figures of the diagnosis a learned case or pattern came from. */
export const STATS_TEXT = Object.freeze({
  heading: "How the diagnosis went",
  rough: "Rough",
  roughHint: "These are estimates read off the diagnosis when it ended, not measurements. They are relative to the causes that were still open.",
  loading: "Loading the diagnosis…",
  none: "No diagnosis figures were stored for this conversation.",
  noCauses: "No causes were scored.",
  learnedPercent: {
    label: "Likelihood at the end",
    explain: (percent) => `Arta rated this cause at about ${percent}% when the diagnosis ended.`,
    how: "The cause's share of the score among the causes still open when the diagnosis ended, as the diagnosis showed it (capped at 95%). Read from the stored summary, not recomputed.",
    missing: "This cause was not among the leading causes when the diagnosis ended.",
  },
  rank: {
    label: "Rank",
    value: (rank, of) => `${ordinal(rank)} of ${of}`,
    explain: (rank, of) => (rank === 1 ? `It led the ${plural(of, "cause")} Arta was weighing.` : `${ordinal(rank)} of the ${plural(of, "cause")} Arta was weighing.`),
    how: "Position of the learned cause among the leading causes, by the percentage each had at the end.",
  },
  margin: {
    label: "Lead over the next cause",
    value: (points) => pts(points),
    explain: (points) => (points > 0 ? `${pts(points)} ahead of the next cause: a fairly clear call.`
      : points < 0 ? `${pts(Math.abs(points))} behind another cause: the engineer decided against the ranking.` : "Tied with another cause."),
    how: "The learned cause's percentage minus the highest percentage of any other leading cause.",
  },
  pinned: "Pinned by the engineer during the diagnosis",
  steps: {
    label: "Steps",
    value: (steps, answered) => (answered < steps ? `${steps} · ${answered} answered` : `${steps}`),
    explain: (steps, answered) => `${plural(steps, "step")} were run, ${answered} got an answer or a result.`,
    how: "Count of the steps in the stored diagnosis summary; answered: the ones with a recorded answer or result.",
  },
  turns: {
    label: "Messages",
    explain: (turns) => `${plural(turns, "message")} in the conversation.`,
    how: "Count of the stored turns of this conversation.",
  },
  facts: {
    label: "Facts",
    value: (confirmed, denied) => `${confirmed} confirmed · ${denied} ruled out`,
    explain: (confirmed, denied) => `${plural(confirmed, "fact")} were confirmed and ${denied} ruled out along the way.`,
    how: "Counts of the confirmed and denied facts in the stored diagnosis summary.",
  },
  ruledOut: {
    label: "Causes ruled out",
    explain: (n) => `${plural(n, "cause")} were set aside before the end.`,
    how: "Count of the causes the diagnosis ruled out, each with its reason.",
  },
  failed: {
    label: "Fixes that did not work",
    explain: (n) => `${plural(n, "fix")} were tried before the one that worked.`,
    how: "Count of the failed proposals recorded on the root cause.",
  },
  causes: {
    heading: "Where each cause stood at the end",
    learned: "Learned",
    more: (n) => `+${n} more with a smaller share`,
    how: "Each cause's share of the score when the diagnosis ended, as the diagnosis showed it. Relative, not a probability.",
  },
  ruledOutHeading: "Ruled out",
  fix: "Fix recorded",
  timeline: {
    heading: "Timeline of the diagnosis",
    empty: "No steps were recorded.",
    noAnswer: "No answer recorded",
    more: (n) => `${n} earlier steps are not shown.`,
    stepLabel: (n) => `Step ${n}`,
  },
});

/** The cause's track record in the graph (the Conversations tab) and what this case adds to it. */
export const TRACK_TEXT = Object.freeze({
  heading: "Cause track record",
  rough: "Rough",
  confidence: {
    label: "Confidence",
    explain: (percent, cases) => `Arta now trusts this cause at about ${percent}%, from ${plural(cases, "solved case")}.`,
    how: "(confirmed + 1) ÷ (confirmed + wrong + 2), where confirmed and wrong are recency-weighted case counts (90-day half-life; a runbook cause starts with one case's weight). Capped at 95%.",
  },
  cases: {
    label: "Solved cases",
    explain: (cases) => `${plural(cases, "case")} confirmed this cause so far, this one included.`,
    how: "Count of the learned cases on the cause in the graph, undone ones excluded.",
  },
  contribution: {
    label: "This case's weight",
    loading: "Working out this case's weight…",
    value: (points) => `${points} pts`,
    explain: (now, after, cases) => `Without this case the confidence would be ${after}% instead of ${now}% (${plural(cases, "case")} left).`,
    explainUndone: (now, after) => `Putting this case back would move the confidence from ${now}% to ${after}%.`,
    how: "The confidence recomputed with this case taken out (or put back for an undone case), the same figure the Undo confirm shows. Depends on how recent the case is.",
    unavailable: "Could not work out this case's weight.",
    retry: "Retry",
  },
  noCause: "This case taught no cause in the graph, so it has no track record.",
  notInScope: "The cause's track record is not loaded.",
});
